/** Shared 2-hour availability slots for all CareHub caregiver roles. */

import { getAllowedWindowsForSearchShift } from "./careShiftWindows";

/**
 * Caregiver roster catalog (used by AttendantAvailabilityHub / weekly roster UI).
 * Existing caregivers already have `profile_extended.attendantCareHub.activeSlotIds`
 * saved against these exact ids — do NOT rename or retime them here.
 */
export const CAREHUB_PREDEFINED_SLOTS = [
  { id: "s0608", start: "06:00", end: "08:00", label: "06:00 AM - 08:00 AM" },
  { id: "s0810", start: "08:00", end: "10:00", label: "08:00 AM - 10:00 AM" },
  { id: "s1012", start: "10:00", end: "12:00", label: "10:00 AM - 12:00 PM" },
  { id: "s1202", start: "12:00", end: "14:00", label: "12:00 PM - 02:00 PM" },
  { id: "s1404", start: "14:00", end: "16:00", label: "02:00 PM - 04:00 PM" },
  { id: "s1606", start: "16:00", end: "18:00", label: "04:00 PM - 06:00 PM" },
  { id: "s1808", start: "18:00", end: "20:00", label: "06:00 PM - 08:00 PM" },
];

/** Default daytime slots (06:00–20:00) for nurse, physio, and attendant. */
export const CAREHUB_DEFAULT_DAYTIME_SLOT_IDS = [
  "s0608",
  "s0810",
  "s1012",
  "s1202",
  "s1404",
  "s1606",
  "s1808",
];

/**
 * Customer-facing 2-hour Hourly booking chips (7:00 AM – 9:00 PM), independent of the
 * caregiver roster catalog above — customer chips are keyed by start/end, never by id.
 */
export const CUSTOMER_HOURLY_SLOTS = [
  { start: "07:00", end: "09:00", label: "07:00 AM - 09:00 AM" },
  { start: "09:00", end: "11:00", label: "09:00 AM - 11:00 AM" },
  { start: "11:00", end: "13:00", label: "11:00 AM - 01:00 PM" },
  { start: "13:00", end: "15:00", label: "01:00 PM - 03:00 PM" },
  { start: "15:00", end: "17:00", label: "03:00 PM - 05:00 PM" },
  { start: "17:00", end: "19:00", label: "05:00 PM - 07:00 PM" },
  { start: "19:00", end: "21:00", label: "07:00 PM - 09:00 PM" },
];

const FIXED_BLOCK_START_HOURS = {
  120: [6, 8, 10, 12, 14, 16, 18],
  240: [6, 10, 14, 18],
  360: [6, 12, 18],
  480: [6, 14],
  720: [6],
};

/** Shift-window chips for 12h / 24h marketplace search (no hourly packages). */
export function getShiftBookingBlocks(searchShift) {
  const shift = String(searchShift || "").toLowerCase();
  const fixedWindows = getAllowedWindowsForSearchShift(shift);
  if (fixedWindows?.length) {
    return fixedWindows.map((w) => ({
      start: w.start,
      end: w.end,
      overnight: Boolean(w.overnight),
      label: w.label || formatCareHubSlotLabel(w.start, w.end),
    }));
  }
  if (shift === "24h") {
    // Hourly live-in start options between 8:00 AM and 6:00 PM.
    const starts = [];
    for (let hour = 8; hour <= 18; hour += 1) {
      const start = `${String(hour).padStart(2, "0")}:00`;
      starts.push({
        start,
        end: start,
        overnight: true,
        label: `Start ${formatCareHubTime(start)}`,
      });
    }
    return starts;
  }
  return null;
}

/** Predefined fixed blocks for a given package duration (minutes). */
export function getFixedBlocksForDuration(durationMinutes = 120, searchShift = "") {
  const shiftBlocks = getShiftBookingBlocks(searchShift);
  if (shiftBlocks?.length) return shiftBlocks;

  const mins = Math.max(60, Number(durationMinutes) || 120);
  if (mins === 120) {
    return CUSTOMER_HOURLY_SLOTS.map((slot) => ({
      start: slot.start,
      end: slot.end,
      label: slot.label,
    }));
  }
  if (mins >= 1440) {
    return getShiftBookingBlocks("24h") || [];
  }
  if (mins >= 720) {
    return getShiftBookingBlocks("12_day") || [];
  }
  const hours = FIXED_BLOCK_START_HOURS[mins] || FIXED_BLOCK_START_HOURS[120];
  const blockHours = mins / 60;
  return hours.map((startHour) => {
    const endHour = startHour + blockHours;
    const pad = (n) => String(n).padStart(2, "0");
    const start = `${pad(startHour)}:00`;
    const end = `${pad(endHour % 24)}:00`;
    return {
      start,
      end,
      label: formatCareHubSlotLabel(start, end),
    };
  });
}

/** Customer booking chips: 07:00–21:00 fixed blocks for the selected duration. */
export function getCustomerBookingBlocksForDuration(durationMinutes = 120, searchShift = "") {
  const shiftBlocks = getShiftBookingBlocks(searchShift);
  if (shiftBlocks?.length) return shiftBlocks;

  return getFixedBlocksForDuration(durationMinutes, searchShift).filter((block) => {
    const startMin = careHubTimeToMinutes(block.start);
    const endMin = careHubTimeToMinutes(block.end);
    if (startMin == null || endMin == null) return false;
    if (endMin <= startMin) return startMin >= 6 * 60; // overnight / 24h live-in
    return startMin >= 7 * 60 && endMin <= 21 * 60;
  });
}

function careHubTimeToMinutes(value) {
  const match = String(value || "").match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
  return hours * 60 + minutes;
}

/** Display a single wall-clock time like "10:00 AM". */
export function formatCareHubTime(value) {
  const minutes = careHubTimeToMinutes(value);
  if (minutes == null) return "";
  const hours24 = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const suffix = hours24 >= 12 ? "PM" : "AM";
  const hours12 = hours24 % 12 || 12;
  return `${String(hours12).padStart(2, "0")}:${String(mins).padStart(2, "0")} ${suffix}`;
}

/** Display label like "10:00 AM - 12:00 PM" for booking slot chips. */
export function formatCareHubSlotLabel(start, end) {
  const startLabel = formatCareHubTime(start);
  const endLabel = formatCareHubTime(end);
  if (!startLabel || !endLabel) return "";
  return `${startLabel} - ${endLabel}`;
}

/** True when API sub-slots fully cover a fixed block (e.g. four 30-min slots cover 6–8). */
export function isFixedBlockCoveredBySlots(blockStart, blockEnd, slotSet) {
  const blockStartMin = careHubTimeToMinutes(blockStart);
  const blockEndMin = careHubTimeToMinutes(blockEnd);
  if (blockStartMin == null || blockEndMin == null) return false;
  // Overnight / same-clock live-in: trust an exact start-end key from the API.
  if (blockEndMin <= blockStartMin) {
    return slotSet.has(`${blockStart}-${blockEnd}`);
  }
  if (slotSet.has(`${blockStart}-${blockEnd}`)) return true;

  const intervals = [];
  for (const key of slotSet) {
    const match = String(key).match(/^(\d{2}:\d{2})-(\d{2}:\d{2})$/);
    if (!match) continue;
    const startMin = careHubTimeToMinutes(match[1]);
    const endMin = careHubTimeToMinutes(match[2]);
    if (startMin == null || endMin == null || endMin <= startMin) continue;
    if (endMin <= blockStartMin || startMin >= blockEndMin) continue;
    intervals.push([Math.max(startMin, blockStartMin), Math.min(endMin, blockEndMin)]);
  }
  if (!intervals.length) return false;

  intervals.sort((a, b) => a[0] - b[0]);
  let cursor = blockStartMin;
  for (const [startMin, endMin] of intervals) {
    if (startMin > cursor) return false;
    cursor = Math.max(cursor, endMin);
    if (cursor >= blockEndMin) return true;
  }
  return cursor >= blockEndMin;
}
